import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { Status } from '@thuas/pd-schemas/built/generated/api-types';
import { ISiteState } from 'features/site/siteSlice';
import { sameId } from 'helpers/objects';
import { IMenu } from './menuAPI';
import {
  deleteMenuItem,
  IMenuItem,
  patchMenuItem,
  postMenuItem,
} from './menuItemAPI';

type Status = (typeof Status)[keyof typeof Status];

export const postMenuItemAsync = createAsyncThunk(
  'menuItem/postMenuItem',
  async (
    {
      data,
    }: {
      data: IMenuItem;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await postMenuItem(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const patchMenuItemAsync = createAsyncThunk(
  'menuItem/patchMenuItem',
  async (
    {
      data,
      fromMenu,
    }: {
      data: IMenuItem;
      fromMenu?: IMenu;
    },
    thunkAPI,
  ) => {
    try {
      const res = { response: await patchMenuItem(data) };
      return res;
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const deleteMenuItemAsync = createAsyncThunk(
  'menuItem/deleteMenuItem',
  async (
    {
      id,
      menuId,
    }: {
      id: number;
      menuId: number;
    },
    thunkAPI,
  ) => {
    try {
      const response = await deleteMenuItem(id);
      return {
        response,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export function addMenuItemCases(builder: ActionReducerMapBuilder<ISiteState>) {
  builder
    .addCase(postMenuItemAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(postMenuItemAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const id = action.payload.response.data._id;
      const menu = state.menus.find((m) => m.id === action.meta.arg.data.menu);
      if (menu) {
        menu.items?.push({ ...action.meta.arg.data, id } as IMenuItem);
      }
    })
    .addCase(postMenuItemAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(patchMenuItemAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(patchMenuItemAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const data = action.meta.arg.data;
      const toMenu = state.menus.find((m) => m.id === data.menu?.id);
      if (!toMenu) return;
      const fromMenu =
        state.menus.find((m) => m.id === action.meta.arg.fromMenu?.id) ??
        toMenu;
      const menuItem = fromMenu.items?.find((i) => i.id === data.id);
      if (!menuItem) return;
      if (!sameId(toMenu, fromMenu)) {
        const i = fromMenu.items?.findIndex((item) => item.id === data.id);
        if (i && i >= 0) fromMenu.items?.splice(i, 1);
        toMenu.items?.push(menuItem);
      }
      Object.assign(menuItem, { ...data, menu: toMenu });
    })
    .addCase(patchMenuItemAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(deleteMenuItemAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(deleteMenuItemAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const menu = state.menus.find((m) => m.id === action.meta.arg.menuId);
      if (menu) {
        menu.items = menu.items?.filter((i) => i.id !== action.meta.arg.id);
      }
    })
    .addCase(deleteMenuItemAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    });
}
